import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { isProduction } from '../../utils/isProduction';

/**
 * MaintenanceNotice - Displays a maintenance notification with countdown
 * 
 * @param {Object} props
 * @param {string} [props.startTime] - Start time in ISO format (e.g., '2025-08-20T14:00:00')
 * @param {string} [props.endTime] - End time in ISO format (e.g., '2025-08-21T14:00:00')
 * @param {string} [props.message] - Custom maintenance message
 * @param {boolean} [props.enabled=true] - Whether to show the maintenance notice
 * @param {boolean} [props.productionOnly=false] - If true, only shows in production environment
 */
export default function MaintenanceNotice({ 
  startTime = new Date().toISOString(),
  endTime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  message = 'We are performing scheduled maintenance to improve our services.',
  enabled = true,
  productionOnly = true
}) {
  // Check if we should show the notice based on environment and settings
  const shouldShow = enabled && (!productionOnly || (productionOnly && isProduction()));
  
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    progress: 0,
    status: 'upcoming' // 'upcoming' | 'active' | 'completed'
  });

  // Format time with proper pluralization
  const formatTimeUnit = (value, unit) => {
    return `${value} ${unit}${value !== 1 ? 's' : ''}`;
  };

  const getTimeRemaining = () => {
    const { days, hours, minutes } = timeLeft;
    
    if (timeLeft.status === 'completed') return 'Maintenance completed';
    
    const parts = [];
    if (days > 0) parts.push(formatTimeUnit(days, 'day'));
    if (hours > 0) parts.push(formatTimeUnit(hours, 'hour'));
    if (minutes > 0 || parts.length === 0) parts.push(formatTimeUnit(minutes, 'minute'));
    
    return timeLeft.status === 'active' 
      ? `Time remaining: ${parts.join(' ')}`
      : `Starts in: ${parts.join(' ')}`;
  };

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Update countdown timer
  useEffect(() => {
    if (!shouldShow) return;
    
    const updateTimer = () => {
      const now = new Date();
      const start = new Date(startTime);
      const end = new Date(endTime);
      const totalDuration = end - start;
      
      if (now < start) {
        // Maintenance is upcoming
        const timeUntilStart = start - now;
        const days = Math.floor(timeUntilStart / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeUntilStart % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeUntilStart % (1000 * 60 * 60)) / (1000 * 60));
        
        setTimeLeft({
          days,
          hours,
          minutes,
          seconds: Math.floor((timeUntilStart % (1000 * 60)) / 1000),
          progress: 0,
          status: 'upcoming'
        });
      } else if (now >= end) {
        // Maintenance is completed
        setTimeLeft(prev => ({
          ...prev,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          progress: 100,
          status: 'completed'
        }));
      } else {
        // Maintenance is active
        const timeRemaining = end - now;
        const elapsed = now - start;
        const progress = Math.min(100, (elapsed / totalDuration) * 100);
        
        const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        
        setTimeLeft({
          days,
          hours,
          minutes,
          seconds: Math.floor((timeRemaining % (1000 * 60)) / 1000),
          progress: Math.round(progress * 10) / 10, // 1 decimal place
          status: 'active'
        });
      }
    };
    
    updateTimer();
    const timerId = setInterval(updateTimer, 1000);
    return () => clearInterval(timerId);
  }, [shouldShow, startTime, endTime]);

  // Early return if we shouldn't show the notice
  if (!shouldShow) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 z-[9999] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-2xl mx-4 overflow-hidden shadow-2xl"
      >
        <div className="p-6 sm:p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6">
              {timeLeft.status === 'completed' ? '✅' : '🛠️'}
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-3">
              {timeLeft.status === 'completed' 
                ? 'Maintenance Completed' 
                : timeLeft.status === 'active' 
                  ? 'System Maintenance in Progress' 
                  : 'Scheduled Maintenance'}
            </h2>
            
            <p className="text-gray-300 mb-6 text-base">
              {message}
            </p>
            
            <div className="bg-gray-700/50 rounded-lg p-5 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div className="p-3 bg-gray-600/30 rounded-lg">
                  <div className="text-xs text-gray-300 mb-1">
                    {timeLeft.status === 'upcoming' ? 'Scheduled Start' : 'Started'}
                  </div>
                  <div className="font-medium text-white">{formatDate(startTime)}</div>
                </div>
                <div className="p-3 bg-gray-600/30 rounded-lg">
                  <div className="text-xs text-gray-300">
                    {timeLeft.status === 'completed' ? 'Completed At' : 'Estimated Completion'}
                  </div>
                  <div className="font-medium text-white">{formatDate(endTime)}</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                    {getTimeRemaining()}
                  </div>
                  <div className="text-sm text-gray-300">
                    {timeLeft.status === 'upcoming' ? 'Until maintenance starts' :
                     timeLeft.status === 'completed' ? 'All systems operational' :
                     'Maintenance in progress'}
                  </div>
                </div>
                
                {timeLeft.status !== 'completed' && (
                  <div className="mt-5">
                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                      <span>0%</span>
                      <span>Progress: {Math.round(timeLeft.progress)}%</span>
                      <span>100%</span>
                    </div>
                    <div className="h-2 bg-gray-600 rounded-full overflow-hidden">
                      <motion.div 
                        className={`h-full ${
                          timeLeft.progress < 50 ? 'bg-gradient-to-r from-green-500 to-blue-500' :
                          timeLeft.progress < 85 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                          'bg-gradient-to-r from-orange-500 to-red-500'
                        }`}
                        initial={{ width: '0%' }}
                        animate={{ width: `${timeLeft.progress}%` }}
                        transition={{ duration: 1 }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="text-sm text-gray-400 mb-4">
              We appreciate your patience during this time. For urgent issues, please contact our support team.
            </div>
            <button 
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors" 
              onClick={() => window.location.href = 'tel:+60172799401'}
            >
              Contact Support
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}